/**
 * n8n Webhook Service
 *
 * Phase 0 — kicks off n8n workflows from the React app. The webhooks themselves
 * live in n8n; URLs are configured via Vite env vars (.env.local, .env.production).
 *
 * Every call here is fire-and-forget. The frontend doesn't wait for the workflow
 * to finish — it just notifies n8n that there's new work to do. n8n then writes
 * results back to Firestore, where onSnapshot subscriptions in the UI pick them
 * up automatically. This keeps the frontend simple and lets workflows take as
 * long as they need.
 *
 * If a webhook URL is missing (e.g. local dev without n8n connected), calls
 * silently no-op — the rest of the app continues working with raw Firestore data.
 *
 * Environment variables (set in .env.local or Firebase hosting config):
 *   VITE_N8N_WEBHOOK_TRL_ANALYSIS       → fires Workflow 4 (TRL AI Analysis)
 *   VITE_N8N_WEBHOOK_PATTERN_DETECTION  → fires Workflow 5 (Pattern Detection)
 *   VITE_N8N_WEBHOOK_CASE_STUDY_INGEST  → fires Workflow 1 (Document Ingest)
 *   VITE_N8N_WEBHOOK_BASE_AUTH_HEADER   → optional: a static auth header value
 *                                          for "Header Auth" trigger nodes
 */

const WEBHOOKS = {
  trlAnalysis: import.meta.env.VITE_N8N_WEBHOOK_TRL_ANALYSIS,
  patternDetection: import.meta.env.VITE_N8N_WEBHOOK_PATTERN_DETECTION,
  caseStudyIngest: import.meta.env.VITE_N8N_WEBHOOK_CASE_STUDY_INGEST,
  orgImport: import.meta.env.VITE_N8N_WEBHOOK_ORG_IMPORT,
};

const AUTH_HEADER = import.meta.env.VITE_N8N_WEBHOOK_BASE_AUTH_HEADER;

/**
 * POST a payload to an n8n webhook. Returns true on 2xx, false otherwise.
 * Never throws — webhook failures are non-blocking by design.
 */
async function postWebhook(url, payload, { label = 'webhook' } = {}) {
  if (!url) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn(`[n8n] ${label} webhook URL not configured — skipping. Payload was:`, payload);
    }
    return { ok: false, reason: 'no_url_configured' };
  }
  try {
    const headers = { 'Content-Type': 'application/json' };
    if (AUTH_HEADER) headers['Authorization'] = AUTH_HEADER;
    const res = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers,
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      console.warn(`[n8n] ${label} returned non-2xx:`, res.status);
      return { ok: false, status: res.status };
    }
    return { ok: true, status: res.status };
  } catch (err) {
    console.warn(`[n8n] ${label} fetch error (workflow may still run; this is fire-and-forget):`, err.message);
    return { ok: false, error: err.message };
  }
}

/* ------------------------------------------------------------------------ */
/* Workflow triggers                                                          */
/* ------------------------------------------------------------------------ */

/**
 * Workflow 4 — TRL AI Analysis.
 * Fired immediately after a TRL is written to Firestore. n8n reads the TRL,
 * calls Claude to suggest severity / REPS quadrant / REF action / tags, then
 * writes the aiAnalysis field back. Frontend's onSnapshot picks up the change.
 *
 * @param {string} trlId - Firestore Hugo_TRLs document ID
 * @param {Object} [context] - Optional metadata for analytics (org, persona, etc.)
 */
export function triggerTrlAnalysis(trlId, context = {}) {
  return postWebhook(
    WEBHOOKS.trlAnalysis,
    { trlId, triggeredAt: new Date().toISOString(), context },
    { label: 'TRL analysis' }
  );
}

/**
 * Workflow 5 — Pattern Detection.
 * Fired after a TRL is approved (DH confirms severity). n8n compares against
 * Hugo_PatternLibrary; if a strong match is found, writes a Hugo_RiskAlerts entry
 * and updates the TRL's patternMatch field.
 */
export function triggerPatternDetection(trlId, context = {}) {
  return postWebhook(
    WEBHOOKS.patternDetection,
    { trlId, triggeredAt: new Date().toISOString(), context },
    { label: 'Pattern detection' }
  );
}

// (Workflow 6 — Divergence Detection — runs on its own 15-minute schedule in n8n; the old
// triggerDivergenceCheck() webhook call was dead code and was removed in P7-12.)

/**
 * Workflows 1–3 — Case Study Analysis Pipeline.
 * Fired after a document upload completes. Workflow 1 ingests + extracts text,
 * triggers Workflow 2 (OE Elements parallel fan-out), then Workflow 3 (Final
 * Synthesis). Each updates Hugo_CaseStudies/cs_{docId} status fields incrementally
 * so the frontend can render section-by-section as analysis completes.
 *
 * @param {string} documentId - Firestore Hugo_Documents document ID
 * @param {Object} document - The uploaded document (downloadURL, fileName, etc.)
 */
export function triggerCaseStudyIngest(documentId, document = {}, context = {}) {
  return postWebhook(
    WEBHOOKS.caseStudyIngest,
    {
      documentId,
      downloadURL: document.downloadURL,
      fileName: document.fileName,
      contentType: document.contentType,
      triggeredAt: new Date().toISOString(),
      context,
    },
    { label: 'Case study ingest' }
  );
}

/**
 * Workflow 7 — Org Chart AI Import.
 * Fired after the org-chart file is uploaded to Storage and the Hugo_OrgImports
 * doc exists. WF7 parses the file with Claude and writes the proposed structure
 * back to the import doc (status: ready_for_review | failed).
 */
export function triggerOrgImport(payload) {
  return postWebhook(WEBHOOKS.orgImport, payload, { label: 'Org import' });
}

/**
 * Test helper — checks which webhooks are configured. Used in OrgITPortal /
 * admin views to show n8n connection status.
 */
export function getN8nStatus() {
  const configured = Object.entries(WEBHOOKS)
    .filter(([, url]) => !!url)
    .map(([key]) => key);
  return {
    configured,
    missing: Object.keys(WEBHOOKS).filter((k) => !WEBHOOKS[k]),
    authConfigured: !!AUTH_HEADER,
  };
}
